import classNames from 'classnames';
import Link from 'next/link';
import React from 'react';
import { ChatRightHeartFill, EnvelopeOpenHeartFill, Github, HeartFill, LifePreserver, Linkedin, RocketTakeoffFill } from 'react-bootstrap-icons';

import { Spacer } from '@/components/util';
import { Col, Container, Row } from '@/components/wrapped';
import { CLAIM, LICENSE_URL, SPACER } from '@/lib/constants';

import styles from '@/styles/Footer.module.scss';

// All footer links point at static or API/CMS-backed pages that aren't worth
// prefetching for every link in the (long) footer, so default prefetch off.
function FooterLink({ children, ...props }: React.ComponentProps<typeof Link>) {
  return <Link {...props} prefetch={false}>{children}</Link>;
}

export default function Footer() {
  return (
    <>
      <div className={classNames("d-print-none", styles.footer)} role="contentinfo">
        <Container>
          <Row>
            {/* <Col md={3}>
              <img
                src="https://assets.opensanctions.org/images/ura/logo_footer.svg"
                alt="OpenSanctions"
                className={styles.logo}
                width="100%"
              />
            </Col> */}
            <Col md={10}>
              <Row>
                <Col md={3}>
                  <strong>What we do</strong>
                  <ul>
                    <li>
                      <FooterLink href="/docs/about/">About OpenSanctions</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="/docs/">Documentation</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="/licensing/">Commercial use</FooterLink>
                    </li>
                  </ul>
                </Col>
                <Col md={3}>
                  <strong>Datasets</strong>
                  <ul>
                    <li>
                      <FooterLink href="/datasets/sanctions/">Global sanctions</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="/datasets/">Specialized datasets</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="https://www.everypolitician.org">EveryPolitician.org</FooterLink>
                    </li>
                  </ul>
                </Col>
                <Col md={3}>
                  <strong>Keep updated</strong>
                  <ul>
                    <li>
                      <FooterLink href="/newsletter/" aria-hidden="true"><EnvelopeOpenHeartFill /></FooterLink>
                      {' '}
                      <FooterLink href="/newsletter/" aria-label='Subscribe to our newsletter'>Newsletter</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="https://www.linkedin.com/company/opensanctions/" aria-hidden="true"><Linkedin /></FooterLink>
                      {' '}
                      <FooterLink href="https://www.linkedin.com/company/opensanctions/">LinkedIn</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="https://github.com/opensanctions/opensanctions" aria-hidden="true"><Github /></FooterLink>
                      {' '}
                      <FooterLink href="https://github.com/opensanctions/opensanctions">Github code</FooterLink>
                    </li>
                  </ul>
                </Col>
                <Col md={3}>
                  <strong>Get in touch</strong>
                  <ul>
                    <li>
                      <FooterLink href="/support/" aria-hidden="true"><LifePreserver /></FooterLink>
                      {' '}
                      <FooterLink href="/support/">Get support</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="/sales/" aria-hidden="true"><RocketTakeoffFill /></FooterLink>
                      {' '}
                      <FooterLink href="/sales/">Talk to sales</FooterLink>
                    </li>
                    <li>
                      <FooterLink href="https://discuss.opensanctions.org" aria-hidden="true"><ChatRightHeartFill /></FooterLink>
                      {' '}
                      <FooterLink href="https://discuss.opensanctions.org">Forum</FooterLink>
                    </li>
                  </ul>
                </Col>
              </Row>
              <Row>
                <p className={styles.copyright}>
                  The data is licensed under
                  the terms of <FooterLink href={LICENSE_URL} rel="license">Creative Commons 4.0 Attribution NonCommercial</FooterLink>
                </p>
                <p className={styles.copyright}>
                  Made with <HeartFill className={styles.love} /> across Europe
                  {SPACER}
                  <FooterLink href="/account/">Customer portal</FooterLink>
                  {SPACER}
                  <FooterLink href="https://status.opensanctions.org">System status</FooterLink>
                  {SPACER}
                  <FooterLink href="/changelog/">Changelog</FooterLink>
                  {SPACER}
                  <FooterLink href="https://trust.opensanctions.org">Trust Center</FooterLink>
                  {SPACER}
                  <FooterLink href="/docs/privacy/">Privacy</FooterLink>
                  {SPACER}
                  <FooterLink href="/docs/security/">Security</FooterLink>
                  {SPACER}
                  <FooterLink href="/llms.txt">For LLMs</FooterLink>
                  {SPACER}
                  <FooterLink href="/impressum/">Impressum</FooterLink>
                </p>
              </Row>
            </Col>
            <Col md={2}>
              <FooterLink href="http://trust.opensanctions.org/">
                <img
                  src="https://assets.opensanctions.org/images/iso27001-consilium.png"
                  alt="We are ISO/IEC 27001:2022 certified by Consilium Labs!"
                  className={styles.logo}
                  width="100%"
                />
              </FooterLink>
            </Col>
          </Row>
        </Container>
      </div>
      <div className={classNames(styles.printFooter, "d-none", "d-print-block")}>
        <Container>
          <div className={styles.footerImprint}>
            <strong>OpenSanctions</strong> <Spacer /> {CLAIM}<br />
            OpenSanctions Datenbanken GmbH <Spacer /> info@opensanctions.org <Spacer /> <FooterLink href="https://www.opensanctions.org/contact/">https://www.opensanctions.org/contact/</FooterLink>
          </div>
        </Container>
      </div >
    </>
  )
}
